import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import {
  Plus,
  Clock,
  Check,
  RotateCcw,
  Edit2,
  Trash2,
  RefreshCw,
  AlertTriangle,
  X,
} from 'lucide-react'

const REASONS = [
  'No Show',
  'Called Out',
  'Short Staffed',
  'Late Arrival',
  'Early Departure',
  'Other',
]

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Parse the YYYY-MM-DD parts by hand so the day never shifts by timezone
const formatDate = (value) => {
  if (!value) return ''
  const [y, mo, day] = value.split('-').map(Number)
  const date = new Date(y, mo - 1, day)
  return `${DAYS[date.getDay()]} ${MONTHS[mo - 1]} ${day}, ${y}`
}

const formatTime = (value) => {
  if (!value) return ''
  const [h, m] = value.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
}

const formatDuration = (minutes) => {
  const mins = parseInt(minutes, 10) || 0
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

const minutesBetween = (start, end) => {
  if (!start || !end) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  return eh * 60 + em - (sh * 60 + sm)
}

const todayISO = () => {
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60000
  return new Date(now.getTime() - offset).toISOString().split('T')[0]
}

const emptyForm = (name = '') => ({
  date: todayISO(),
  startTime: '',
  endTime: '',
  reason: 'No Show',
  notes: '',
  loggedBy: name,
})

const LifeguardGaps = () => {
  const { user } = useAuth()
  const [gaps, setGaps] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState('')
  const [filter, setFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [editingGap, setEditingGap] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState(emptyForm())

  const fetchGaps = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const { data } = await axios.get('/api/lifeguard-gaps')
      setGaps(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load lifeguard coverage gaps')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchGaps()
  }, [fetchGaps])

  const showToast = (message) => {
    setToast(message)
    setTimeout(() => setToast(''), 2800)
  }

  const openModal = (gap = null) => {
    if (gap) {
      setEditingGap(gap)
      setFormData({
        date: gap.date,
        startTime: gap.startTime,
        endTime: gap.endTime,
        reason: gap.reason,
        notes: gap.notes || '',
        loggedBy: gap.loggedBy || '',
      })
    } else {
      setEditingGap(null)
      setFormData(emptyForm(user?.name || ''))
    }
    setError(null)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingGap(null)
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (minutesBetween(formData.startTime, formData.endTime) <= 0) {
      setError('End time must be after start time')
      return
    }

    try {
      setSaving(true)
      setError(null)

      if (editingGap) {
        await axios.put(`/api/lifeguard-gaps/${editingGap._id}`, formData)
        showToast('Entry updated')
      } else {
        await axios.post('/api/lifeguard-gaps', formData)
        showToast(
          `Gap logged — ${formatDuration(minutesBetween(formData.startTime, formData.endTime))} uncovered`
        )
      }

      await fetchGaps()
      closeModal()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save entry')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleResolveToggle = async (gap) => {
    try {
      const action = gap.resolved ? 'unresolve' : 'resolve'
      await axios.patch(`/api/lifeguard-gaps/${gap._id}/${action}`)
      await fetchGaps()
      showToast(gap.resolved ? 'Marked open' : 'Marked resolved')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update entry')
      console.error(err)
    }
  }

  const handleDelete = async (gap) => {
    if (!confirm('Delete this entry? This cannot be undone.')) return

    try {
      await axios.delete(`/api/lifeguard-gaps/${gap._id}`)
      await fetchGaps()
      showToast('Entry deleted')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry')
      console.error(err)
    }
  }

  const total = gaps.length
  const open = gaps.filter((g) => !g.resolved).length
  const resolved = total - open
  const totalHours = (gaps.reduce((sum, g) => sum + (parseInt(g.duration, 10) || 0), 0) / 60).toFixed(1)

  const visibleGaps = gaps.filter((g) => {
    if (filter === 'open') return !g.resolved
    if (filter === 'resolved') return g.resolved
    return true
  })

  const previewDuration = minutesBetween(formData.startTime, formData.endTime)

  const stats = [
    { label: 'Total Gaps', value: total, danger: false },
    { label: 'Hrs Uncovered', value: totalHours, danger: true },
    { label: 'Open', value: open, danger: open > 0 },
    { label: 'Resolved', value: resolved, danger: false },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Lifeguard Coverage</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Log and track periods the pool was open without lifeguard coverage
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          Log a Gap
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`bg-white rounded-xl shadow-md p-4 border-t-4 ${
              stat.danger ? 'border-red-500' : 'border-primary-500'
            }`}
          >
            <p
              className={`text-2xl sm:text-3xl font-bold ${
                stat.danger ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {loading ? '–' : stat.value}
            </p>
            <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Error banner */}
      {error && !showModal && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 flex-1 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors ${
              filter === f.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-500 hover:text-primary-600'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={fetchGaps}
          className="ml-auto px-4 py-2 rounded-full text-sm font-semibold border border-gray-300 bg-white text-gray-600 hover:border-primary-500 hover:text-primary-600 flex items-center gap-1.5 whitespace-nowrap transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Gap log */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading coverage log...</p>
        </div>
      ) : visibleGaps.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-10 text-center">
          <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">
            {total === 0 ? 'No coverage gaps logged yet.' : 'No entries match this filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleGaps.map((gap) => (
            <div
              key={gap._id}
              className={`bg-white rounded-xl shadow-md p-4 border-l-4 ${
                gap.resolved ? 'border-green-600' : 'border-red-600'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900">{formatDate(gap.date)}</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {formatTime(gap.startTime)} &rarr; {formatTime(gap.endTime)}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-md text-sm font-semibold whitespace-nowrap ${
                    gap.resolved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {formatDuration(gap.duration)}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide bg-primary-50 text-primary-700 border border-primary-100">
                  {gap.reason}
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    gap.resolved ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {gap.resolved ? 'Resolved' : 'Open'}
                </span>
                {gap.loggedBy && (
                  <span className="text-xs text-gray-500">by {gap.loggedBy}</span>
                )}
              </div>

              {gap.notes && (
                <p className="text-sm text-gray-600 italic mt-2 break-words">{gap.notes}</p>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => openModal(gap)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold border border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleResolveToggle(gap)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border flex items-center gap-1.5 transition-colors ${
                    gap.resolved
                      ? 'border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white'
                      : 'border-green-600 text-green-700 hover:bg-green-600 hover:text-white'
                  }`}
                >
                  {gap.resolved ? (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Reopen
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Resolve
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDelete(gap)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-400 hover:border-red-500 hover:text-red-600 flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {editingGap ? 'Edit Entry' : 'Log a Coverage Gap'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>

                {previewDuration > 0 && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-primary-600" />
                    {formatDuration(previewDuration)} uncovered
                  </p>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {REASONS.map((reason) => (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => setFormData({ ...formData, reason })}
                        className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors ${
                          formData.reason === reason
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-primary-500 hover:text-primary-600'
                        }`}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Logged By <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.loggedBy}
                    onChange={(e) => setFormData({ ...formData, loggedBy: e.target.value })}
                    className="input"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input"
                    rows="3"
                    placeholder="Who was scheduled? Any context..."
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-outline flex-1"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary flex-1" disabled={saving}>
                    {saving ? 'Saving...' : editingGap ? 'Save Changes' : 'Log Gap'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg z-[60] max-w-[92vw] text-center">
          {toast}
        </div>
      )}
    </div>
  )
}

export default LifeguardGaps
